import { useEffect, useRef } from 'react';

export interface UseEscapeToCloseOptions {
  isOpen: boolean;
  onClose: (() => void) | undefined;
  canClose?: boolean;
}

type OpenOverlay = { close: () => void; canClose: boolean };

const openOverlays: OpenOverlay[] = [];

function closeInnermostOnEscape(event: KeyboardEvent) {
  if (event.defaultPrevented || event.key !== 'Escape') return;
  const innermost = openOverlays.at(-1);
  if (innermost && innermost.canClose) innermost.close();
}

/**
 * Closes an open overlay when Escape is pressed anywhere in the document.
 *
 * Only the overlay opened last closes, so an editor or modal nested in another
 * modal closes alone. Open order, not DOM nesting, decides this: a nested
 * overlay must open after its parent, not in the same render. An Escape a nested field already handled — a combobox
 * collapsing its list — is left alone too. Pass `canClose: false` to hold the
 * overlay open regardless, e.g. mid-save; overlays beneath it stay open as well.
 *
 * @example
 *   useEscapeToClose({ isOpen, onClose: onDone, canClose: !isSaving });
 */
export function useEscapeToClose({
  isOpen,
  onClose,
  canClose = true,
}: UseEscapeToCloseOptions): void {
  const overlay = useRef<OpenOverlay>({ close: () => {}, canClose });
  const hasOnClose = onClose !== undefined;

  useEffect(() => {
    overlay.current.close = () => onClose?.();
    overlay.current.canClose = canClose;
  });

  useEffect(() => {
    if (!isOpen || !hasOnClose) return;
    const entry = overlay.current;
    openOverlays.push(entry);
    if (openOverlays.length === 1) {
      document.addEventListener('keydown', closeInnermostOnEscape);
    }
    return () => {
      openOverlays.splice(openOverlays.indexOf(entry), 1);
      if (openOverlays.length === 0) {
        document.removeEventListener('keydown', closeInnermostOnEscape);
      }
    };
  }, [isOpen, hasOnClose]);
}
